import {useCallback, useEffect, useState} from 'react';
import {useFormContext} from 'react-hook-form';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {GetProfessionalParams, ModalityCodeEnum, ParsedProfessionals} from 'domain/usecases';
import {OperationStateTypesEnum} from 'presentation/constants';
import {FeaturesEnum} from 'presentation/constants/featuresFlagEnum';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import {validateCNPJ, validateCPF} from 'presentation/utils';
import {useRefundRequest} from '../../../hooks';
import {
	CouncilData,
	CouncilState,
	PersonType,
	PersonTypeEnum,
	SearchDoctorBy,
	SearchDoctorByEnum,
} from '../components/ReceiptRegistration/components/ProviderData/constants/types';
import {useAccordionExpandedStore} from '../store/useAccordionExpandedStore';
import {useSelectedDoctorStore} from '../store/useSelectedDoctorStore';
import {useSelectedProfessionalStore} from '../store/useSelectedProfessionalStore';
import {useSelectedProviderStore} from '../store/useSelectedProviderStore';
import {useStepStore} from '../store/useStepStore';
import {AccordionExpandedEnum, StepEnum} from '../types';
import {useProfessionalCouncilOptionsStore} from '../store/useProfessionalCouncilOptionsStore';

const {invalidCPF, invalidCNPJ} =
	translations['pt-br'].privateFlow.refund.receiptRegistrationPage.providerData.errors;

const {unauthorizedProviderModal} =
	translations['pt-br'].privateFlow.refund.receiptRegistrationPage.providerData;

const {requiredField} = translations['pt-br'].commons;

export function useProviderSearch() {
	const accordionExpanded = useAccordionExpandedStore(state => state.accordion);
	const setAccordionExpanded = useAccordionExpandedStore(state => state.setAccordion);
	const {beneficiary} = useAccountDataStore(store => store.accountData);
	const {isEnabled: isAlphanumericDocumentEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_ALPHANUMERIC_DOCUMENT,
	);
	const [doctorsList, setDoctorsList] = useState<ParsedProfessionals[]>([]);
	const [doctorsListProfessional, setDoctorsListProfessional] = useState<ParsedProfessionals[]>(
		[],
	);
	const [requestState, setRequestState] = useState(OperationStateTypesEnum.NONE);
	const [requestStateProfessional, setRequestStateProfessional] = useState(
		OperationStateTypesEnum.NONE,
	);

	const professionalCouncilOptions = useProfessionalCouncilOptionsStore(state => state.options);
	const {
		getProfessional,
		requests,
		atoms: {treatmentData},
	} = useRefundRequest();
	const [totalItems, setTotalItems] = useState(0);
	const [totalItemsProfessional, setTotalItemsProfessional] = useState(0);
	const resetSelectedDoctor = useSelectedDoctorStore(state => state.reset);
	const resetSelectedProfessional = useSelectedProfessionalStore(state => state.reset);
	const resetSelectedProvider = useSelectedProviderStore(state => state.reset);
	const [isProviderUnauthorizedState, setIsProviderUnauthorizedState] = useState(false);
	const setStep = useStepStore(state => state.setStep);

	const {control, watch, resetField, clearErrors, setValue, trigger, setError} = useFormContext();

	const personTypeWatch = watch('professionalType') as PersonType;
	const searchDoctorByWatch = watch('searchDoctorBy') as SearchDoctorBy;
	const doctorNotFoundByWatch = watch('doctorNotFound') as boolean;
	const professionalNotFoundByWatch = watch('professionalNotFound') as boolean;
	const councilAcronymByWatch = watch('councilAcronym') as CouncilData;
	const councilNumberByWatch = watch('councilNumber') as string;
	const councilStateByWatch = watch('councilState') as CouncilState;
	const professionalDocumentByWatch = watch('professionalDocument') as string;
	const professionalCpfByWatch = watch('professionalCpf') as string;
	const providerUnauthorizedWatch = watch('providerUnauthorized');

	const IS_LOADING = requestState === OperationStateTypesEnum.SUBMITTING;
	const IS_ERROR = requestState === OperationStateTypesEnum.ERROR;
	const IS_LOADING_PROFESSIONAL = requestStateProfessional === OperationStateTypesEnum.SUBMITTING;
	const IS_ERROR_PROFESSIONAL = requestStateProfessional === OperationStateTypesEnum.ERROR;
	const MEDICAL_APPOINTMENT_MODALITY =
		treatmentData.modalityCode.toString() === ModalityCodeEnum.MEDICAL_APPOINTMENT;

	const hasMoreThanOneRequest = requests.requestData?.length > 0;

	const unauthorizedProviderModalPrimaryAction = hasMoreThanOneRequest
		? () => setStep(StepEnum.REVIEW)
		: () => setStep(StepEnum.SERVICE_TYPE);

	const unauthorizedProviderModalPrimaryText = hasMoreThanOneRequest
		? unauthorizedProviderModal.backToReview
		: unauthorizedProviderModal.primaryActionText;

	const handleCloseUnauthorizedProviderModal = () => setIsProviderUnauthorizedState(false);

	useEffect(() => {
		if (providerUnauthorizedWatch) {
			setIsProviderUnauthorizedState(true);
			return;
		}
		setIsProviderUnauthorizedState(false);
	}, [providerUnauthorizedWatch]);

	const handleResetDoctorList = (searchInProvider?: boolean) => {
		if (searchInProvider) {
			setDoctorsListProfessional([]);
			setTotalItemsProfessional(0);
			return;
		}
		setDoctorsList([]);
		setTotalItems(0);
	};

	const handleReset = (searchInProvider?: boolean) => {
		if (searchInProvider) {
			clearErrors();
			resetSelectedProfessional();
			setValue('professionalName', '');
			setValue('professionalCode', '');
			setValue('professionalCpf', '');
			setValue('councilAcronym', {
				acronym: '',
				description: '',
			});
			setValue('councilNumber', '');
			setValue('councilState', {
				code: '',
				state: '',
			});
			setValue('professionalNotFound', false);
			setValue('providerUnauthorized', false);
			setValue('providerDocumentMatchUserCpf', false);
			handleResetDoctorList(searchInProvider);
			return;
		}
		clearErrors();
		resetSelectedDoctor();
		resetSelectedProvider();
		setValue('providerName', '');
		setValue('professionalCode', '');
		setValue('professionalName', '');
		setValue('professionalDocument', '');
		setValue('professionalCpf', '');
		setValue('providerCode', '');
		setValue('councilAcronym', {
			acronym: '',
			description: '',
		});
		setValue('councilNumber', '');
		setValue('councilState', {
			code: '',
			state: '',
		});
		setValue('doctorNotFound', false);
		setValue('providerUnauthorized', false);
		setValue('providerDocumentMatchUserCpf', false);
		handleResetDoctorList();
	};

	const handleSearch = async (searchInProvider?: boolean) => {
		try {
			if (searchInProvider) {
				resetSelectedProfessional();
				setValue('professionalName', '');
				setValue('professionalCode', '');
				setValue('professionalNotFound', false);
				handleResetDoctorList(true);
			} else {
				resetSelectedDoctor();
				resetSelectedProvider();
				setValue('providerName', '');
				setValue('providerCode', '');
				setValue('professionalName', '');
				setValue('professionalCode', '');
				setValue('doctorNotFound', false);
				setValue('providerDocumentMatchUserCpf', false);
				handleResetDoctorList();
			}
			setValue('providerUnauthorized', false);

			const searchByDocument =
				searchDoctorByWatch === SearchDoctorByEnum.DOCUMENT ||
				(!searchInProvider && personTypeWatch === PersonTypeEnum.LEGAL_PERSON);

			const searchByCouncilNumber =
				(searchDoctorByWatch === SearchDoctorByEnum.COUNCIL_NUMBER &&
					personTypeWatch === PersonTypeEnum.PHYSICAL_PERSON) ||
				(personTypeWatch === PersonTypeEnum.LEGAL_PERSON &&
					searchDoctorByWatch === SearchDoctorByEnum.COUNCIL_NUMBER &&
					searchInProvider);

			if (searchByDocument) {
				handleResetDoctorList(true);

				if (personTypeWatch === PersonTypeEnum.LEGAL_PERSON && !searchInProvider) {
					setValue('searchDoctorBy', SearchDoctorByEnum.DOCUMENT);
					clearErrors('councilAcronym');
					clearErrors('councilState');
					clearErrors('councilNumber');
				}

				const document =
					searchInProvider || personTypeWatch === PersonTypeEnum.PHYSICAL_PERSON
						? professionalCpfByWatch
						: professionalDocumentByWatch;

				const isDocumentEmpty = !document || document?.length === 0;

				if (personTypeWatch === PersonTypeEnum.LEGAL_PERSON && !searchInProvider) {
					const isCNPJValid = validateCNPJ(document, isAlphanumericDocumentEnabled);
					if (!isCNPJValid) {
						setError('professionalDocument', {
							message: isDocumentEmpty ? requiredField : invalidCNPJ,
						});
						return;
					}
				} else {
					const isCPFValid = validateCPF(document);
					if (!isCPFValid) {
						if (
							searchInProvider ||
							personTypeWatch === PersonTypeEnum.PHYSICAL_PERSON
						) {
							setError('professionalCpf', {
								message: isDocumentEmpty ? requiredField : invalidCPF,
							});
						} else {
							setError('professionalDocument', {
								message: isDocumentEmpty ? requiredField : invalidCPF,
							});
						}
						return;
					}
				}

				clearErrors('professionalDocument');
				clearErrors('professionalCpf');

				const isLegalPersonDocument =
					personTypeWatch === PersonTypeEnum.LEGAL_PERSON && !searchInProvider;
				const documentSanitized = isLegalPersonDocument
					? document.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
					: document.replace(/\D/g, '');

				if (beneficiary?.nuCpfC === documentSanitized) {
					setValue('providerDocumentMatchUserCpf', true);
					return;
				}

				if (searchInProvider) {
					setRequestStateProfessional(OperationStateTypesEnum.SUBMITTING);
				} else {
					setRequestState(OperationStateTypesEnum.SUBMITTING);
				}

				const data = await getProfessional({
					document: documentSanitized,
					'modality-code': MEDICAL_APPOINTMENT_MODALITY
						? treatmentData.modalityCode
						: undefined,
					councils: professionalCouncilOptions,
				});

				setValue(
					searchInProvider ? 'professionalNotFound' : 'doctorNotFound',
					data.content.providers.length === 0,
				);

				if (searchInProvider) {
					setDoctorsListProfessional(data.content?.providers);
					setTotalItemsProfessional(data.content?.total);
				} else {
					setDoctorsList(data.content?.providers);
					setTotalItems(data.content?.total);
				}
			}

			if (searchByCouncilNumber) {
				const isCouncilAcronymValid = await trigger('councilAcronym');
				const isCouncilStateValid = await trigger('councilState');
				const isCouncilNumberValid = await trigger('councilNumber');

				const isValid = await Promise.all([
					isCouncilAcronymValid,
					isCouncilStateValid,
					isCouncilNumberValid,
				]);

				if (!isValid.every(Boolean)) {
					return;
				}

				if (searchInProvider) {
					setRequestStateProfessional(OperationStateTypesEnum.SUBMITTING);
				} else {
					setRequestState(OperationStateTypesEnum.SUBMITTING);
				}

				const data = await getProfessional({
					'council-acronym': councilAcronymByWatch.acronym,
					'council-number': councilNumberByWatch,
					'modality-code': MEDICAL_APPOINTMENT_MODALITY
						? treatmentData.modalityCode
						: undefined,
					state: councilStateByWatch.code,
					councils: professionalCouncilOptions,
				});

				setValue(
					searchInProvider ? 'professionalNotFound' : 'doctorNotFound',
					data.content.providers.length === 0,
				);

				if (searchInProvider) {
					setDoctorsListProfessional(data.content?.providers);
					setTotalItemsProfessional(data.content?.total);
				} else {
					setDoctorsList(data.content?.providers);
					setTotalItems(data.content?.total);
				}
			}

			setRequestStateProfessional(OperationStateTypesEnum.DONE);
			setRequestState(OperationStateTypesEnum.DONE);
		} catch (err) {
			if (err instanceof APIError && err.statusCode === HttpStatusCode.unprocessable) {
				setValue('providerUnauthorized', true);
				setRequestStateProfessional(OperationStateTypesEnum.DONE);
				setRequestState(OperationStateTypesEnum.DONE);
				return;
			}
			if (searchInProvider) {
				setRequestStateProfessional(OperationStateTypesEnum.ERROR);
			} else {
				setRequestState(OperationStateTypesEnum.ERROR);
			}
		}
	};

	const handleClickAccordion = () => {
		if (accordionExpanded > AccordionExpandedEnum.PROVIDER_DATA) {
			setAccordionExpanded(AccordionExpandedEnum.PROVIDER_DATA);
		}
	};

	const handleSeeMore = useCallback(
		async ({page = 1, limit = 5, document, ...params}: GetProfessionalParams) =>
			getProfessional({
				'modality-code': MEDICAL_APPOINTMENT_MODALITY
					? treatmentData.modalityCode
					: undefined,
				limit,
				page,
				document,
				councils: professionalCouncilOptions,
				...params,
			}),
		[MEDICAL_APPOINTMENT_MODALITY, professionalCouncilOptions],
	);

	return {
		handleClickAccordion,
		handleSearch,
		handleReset,
		IS_LOADING,
		IS_LOADING_PROFESSIONAL,
		doctorsList,
		doctorsListProfessional,
		setDoctorsListProfessional,
		personTypeWatch,
		doctorNotFoundByWatch,
		professionalNotFoundByWatch,
		control,
		setValue,
		searchDoctorByWatch,
		handleResetDoctorList,
		resetField,
		accordionExpanded,
		totalItems,
		totalItemsProfessional,
		handleSeeMore,
		setDoctorsList,
		IS_ERROR,
		IS_ERROR_PROFESSIONAL,
		setRequestState,
		setRequestStateProfessional,
		handleCloseUnauthorizedProviderModal,
		unauthorizedProviderModalPrimaryAction,
		unauthorizedProviderModalPrimaryText,
		isProviderUnauthorizedState,
		hasMoreThanOneRequest,
	};
}
